/**
 * Settings › Returns: the reasons a return can give, the default return
 * window, and whether buyer requests are approved without a person.
 *
 * A reason once offered can be switched off but not removed, because
 * returns may already carry its code; the server refuses a removal.
 */
import { useEffect, useState } from 'react';
import {
  ActionIcon, Alert, Button, Card, Group, Loader, NumberInput, Stack, Switch, Text, TextInput, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useReturnsSettings, useSaveReturnsSettings, type ReturnReason, type ReturnsSettings } from '../../api/returns';

type Draft = Omit<ReturnsSettings, 'reasons'> & { reasons: ReturnReason[] };
import { useVocab } from '../../vocab';

/** A code from a label: "Bad postcode" is bad_postcode. */
export function codeFor(label: string): string {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
}

export function ReturnsSettingsTab() {
  const v = useVocab();
  const { data, isPending, error } = useReturnsSettings();
  const save = useSaveReturnsSettings();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    if (data) {
      setDraft({ ...data, reasons: data.reasons ?? [] });
      setSaved(new Set((data.reasons ?? []).map((r) => r.code)));
    }
  }, [data]);

  if (isPending || !draft) return error ? <Alert color="red" variant="light">{error.message}</Alert> : <Loader />;

  const add = () => {
    const code = codeFor(newLabel);
    if (!code || draft.reasons.some((r) => r.code === code)) return;
    setDraft({ ...draft, reasons: [...draft.reasons, { code, label: newLabel.trim(), active: true }] });
    setNewLabel('');
  };

  async function submit() {
    if (!draft) return;
    try {
      await save.mutateAsync(draft);
      notifications.show({ color: 'teal', title: 'Saved', message: 'Return settings saved.' });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not saved', message: (e as Error).message });
    }
  }

  return (
    <Stack maw={720}>
      <Card withBorder padding="lg">
        <Stack>
          <Title order={4}>Reasons</Title>
          <Text size="sm" c="dimmed">
            {`What a ${v.one('buyer')} or your team picks when returning a ${v.one('lead')}. Switch one off to stop it being offered; returns that used it keep it.`}
          </Text>
          {draft.reasons.map((r, i) => (
            <Group key={r.code} gap="sm" wrap="nowrap">
              <TextInput
                aria-label={`Label for ${r.code}`}
                style={{ flex: 1 }}
                value={r.label}
                onChange={(e) => {
                  const reasons = [...draft.reasons];
                  reasons[i] = { ...r, label: e.currentTarget.value };
                  setDraft({ ...draft, reasons });
                }}
              />
              <Text size="xs" c="dimmed" ff="monospace" w={140} truncate>{r.code}</Text>
              <Switch
                aria-label={`Offer ${r.label}`}
                checked={r.active}
                onChange={(e) => {
                  const reasons = [...draft.reasons];
                  reasons[i] = { ...r, active: e.currentTarget.checked };
                  setDraft({ ...draft, reasons });
                }}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label={`Remove ${r.label}`}
                disabled={saved.has(r.code)}
                title={saved.has(r.code) ? 'Saved reasons can be switched off, not removed' : undefined}
                onClick={() => setDraft({ ...draft, reasons: draft.reasons.filter((x) => x.code !== r.code) })}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}
          <Group gap="sm">
            <TextInput
              placeholder="A new reason"
              value={newLabel}
              onChange={(e) => setNewLabel(e.currentTarget.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
              style={{ flex: 1 }}
            />
            <Button variant="default" leftSection={<IconPlus size={16} />} onClick={add} disabled={!codeFor(newLabel)}>
              Add
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder padding="lg">
        <Stack>
          <Title order={4}>Window and approval</Title>
          <NumberInput
            label="Return window"
            description={`Days after a sale a ${v.one('buyer')} may return it, unless their own page says otherwise. 0 means no returns.`}
            suffix=" days"
            min={0}
            max={365}
            w={220}
            value={draft.default_window_days}
            onChange={(n) => setDraft({ ...draft, default_window_days: typeof n === 'number' ? n : 0 })}
          />
          <Switch
            label={`Approve ${v.one('buyer')} requests automatically`}
            description="Inside the window, a request is returned straight away instead of waiting for someone to approve it."
            checked={draft.auto_approve_buyer_requests}
            onChange={(e) => setDraft({ ...draft, auto_approve_buyer_requests: e.currentTarget.checked })}
          />
        </Stack>
      </Card>

      <Group justify="flex-end">
        <Button loading={save.isPending} onClick={submit}>Save</Button>
      </Group>
    </Stack>
  );
}
